<template>
  <article class="page-link-card">
    <aside v-if="item.tags && item.tags.length" class="card-tags">
      <span v-for="(tag, index) in item.tags" :key="tag" class="tag"
        ><a :href="`/tags/${encodeURIComponent(tag)}`" class="tag-link">{{
          tag
        }}</a
        ><span v-if="index < item.tags.length - 1" class="tag-separator">
          /
        </span></span
      >
    </aside>

    <div class="card-content">
      <a v-if="image" :href="item.url" class="card-image">
        <ResponsiveImage
          :src="image"
          :sizes="CARD_SIZES"
          :alt="item.title"
          width="560"
          height="315"
        />
      </a>

      <header class="card-header">
        <time v-if="item.date" class="card-date">
          <span class="date-arrow">►</span>
          {{ formatDate(item.date) }}
        </time>
      </header>

      <h3>
        <a :href="item.url">{{ item.title }}</a>
      </h3>

      <p v-if="item.desc" class="card-desc">
        {{ item.desc }}
      </p>

      <footer class="card-footer">
        <a :href="item.url" class="card-link">
          <span class="slashes">////</span>
          {{ linkText }}
        </a>
      </footer>
    </div>
  </article>
</template>

<script setup lang="ts">
import { formatDate } from "../../../utils/index.ts";
import ResponsiveImage from "../../primitives/ResponsiveImage.vue";

withDefaults(
  defineProps<{
    item: {
      url: string;
      title: string;
      date?: string;
      desc?: string;
      tags?: string[];
    };
    linkText?: string;
    image?: string;
  }>(),
  {
    linkText: "Read more",
    image: undefined,
  },
);

// Cards sit in a repeat(auto-fit, minmax(400px, 1fr)) grid inside a 72rem
// column, so they land near 560px on desktop and full-bleed on mobile.
const CARD_SIZES = "(min-width: 48rem) 560px, 100vw";
</script>

<style scoped lang="scss">
@use "../../styles/abstracts/mixins";

.page-link-card {
  @include mixins.surface;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1.5rem;
  padding: 1rem;
}

.card-tags {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  padding-right: 1rem;
  border-right: 1px solid var(--color-text-base);
  text-transform: lowercase;
  letter-spacing: 0.08em;
}

.tag {
  white-space: nowrap;
}

.tag-link {
  color: var(--color-text-muted);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: var(--color-accent);
    text-decoration: underline;
  }
}

.tag-separator {
  font-weight: 400;
}

.card-content {
  display: flex;
  flex-direction: column;
}

.card-header {
  margin-bottom: 0.5em;
}

.card-date {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: var(--font-size-2xs);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-family: var(--font-mono);
}

.date-arrow {
  font-size: 0.75em;
  font-family: var(--font-family-body);
}

.page-link-card h3 a {
  text-decoration: none;
}

.page-link-card h3 a:hover {
  color: var(--color-accent);
  text-decoration: underline;
}

.card-desc {
  margin: 0;
  flex-grow: 1;
}

.card-footer {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-text-base);
}

.card-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: var(--color-accent);

    .slashes {
      letter-spacing: 0.15em;
    }
  }
}

.slashes {
  font-weight: 400;
  letter-spacing: 0.05em;
  transition: letter-spacing 0.3s cubic-bezier(0.85, 0.09, 0.15, 0.91);
}

.card-image {
  display: block;
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.25s ease;
}

.card-image:hover img {
  transform: scale(1.02);
}
</style>
